import { openDB } from "idb";
import { DEFAULT_SPOT_ID, isSpotId } from "./spots";
import type {
  DataSnapshot,
  PendingReport,
  SavedRegionPack,
  SpotId,
  UserNeeds,
} from "./types";

const DATABASE_NAME = "sanitation-72";
const DATABASE_VERSION = 1;
const LEGACY_SAVED_REGION_KEY = "ariake-13108";
const savedRegionKey = (spotId: SpotId) => `spot:${spotId}`;
const DEVICE_ID_KEY = "anonymous-device-id";
const USER_PREFERENCES_KEY = "user-preferences";
const USER_PREFERENCES_VERSION = 1 as const;

type StoredUserPreferences = {
  version: typeof USER_PREFERENCES_VERSION;
  needs: UserNeeds;
  favoriteToiletIds: string[];
};

export const DEFAULT_USER_NEEDS: Readonly<UserNeeds> = Object.freeze({
  wheelchair: false,
  ostomate: false,
  baby_changing: false,
  openNow: false,
});

const createDefaultPreferences = (): StoredUserPreferences => ({
  version: USER_PREFERENCES_VERSION,
  needs: { ...DEFAULT_USER_NEEDS },
  favoriteToiletIds: [],
});

const normalizeUserNeeds = (value: unknown): UserNeeds | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<UserNeeds>;
  if (
    typeof candidate.wheelchair !== "boolean" ||
    typeof candidate.ostomate !== "boolean" ||
    typeof candidate.baby_changing !== "boolean" ||
    typeof candidate.openNow !== "boolean"
  ) {
    return undefined;
  }
  return {
    wheelchair: candidate.wheelchair,
    ostomate: candidate.ostomate,
    baby_changing: candidate.baby_changing,
    openNow: candidate.openNow,
  };
};

const normalizeFavoriteToiletIds = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const normalized = value.map((id) =>
    typeof id === "string" ? id.trim() : "",
  );
  if (normalized.some((id) => id.length === 0)) return undefined;
  return [...new Set(normalized)];
};

const normalizePreferences = (
  value: unknown,
): StoredUserPreferences | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<StoredUserPreferences>;
  if (candidate.version !== USER_PREFERENCES_VERSION) return undefined;
  const needs = normalizeUserNeeds(candidate.needs);
  const favoriteToiletIds = normalizeFavoriteToiletIds(
    candidate.favoriteToiletIds,
  );
  if (!needs || !favoriteToiletIds) return undefined;
  return {
    version: USER_PREFERENCES_VERSION,
    needs,
    favoriteToiletIds,
  };
};

const normalizeToiletId = (toiletId: string) => {
  const normalized = toiletId.trim();
  if (!normalized) throw new TypeError("toiletId must not be empty");
  return normalized;
};

let database: ReturnType<typeof openDB> | undefined;
const getDatabase = () => {
  database ??= openDB(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("regions")) db.createObjectStore("regions");
      if (!db.objectStoreNames.contains("reports"))
        db.createObjectStore("reports", { keyPath: "id" });
      if (!db.objectStoreNames.contains("metadata"))
        db.createObjectStore("metadata");
    },
  });
  return database;
};

export async function saveRegion(snapshot: DataSnapshot) {
  const pack: SavedRegionPack = {
    version: 1,
    savedAt: new Date().toISOString(),
    snapshot,
  };
  await (await getDatabase()).put(
    "regions",
    pack,
    savedRegionKey(snapshot.region.spotId),
  );
  return pack;
}

const isSavedRegionPack = (value: unknown): value is SavedRegionPack => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SavedRegionPack>;
  return (
    candidate.version === 1 &&
    typeof candidate.savedAt === "string" &&
    Number.isFinite(Date.parse(candidate.savedAt)) &&
    typeof candidate.snapshot?.region?.spotId === "string" &&
    isSpotId(candidate.snapshot.region.spotId)
  );
};

/**
 * Moves the former single-slot save into its spot-specific slot. The read,
 * write and legacy deletion share one transaction so an interrupted migration
 * cannot lose the only saved copy.
 */
const migrateLegacySavedRegion = async () => {
  const db = await getDatabase();
  const transaction = db.transaction("regions", "readwrite");
  const legacy = await transaction.store.get(LEGACY_SAVED_REGION_KEY);
  if (isSavedRegionPack(legacy)) {
    const nextKey = savedRegionKey(legacy.snapshot.region.spotId);
    const current = await transaction.store.get(nextKey);
    if (
      !isSavedRegionPack(current) ||
      Date.parse(legacy.savedAt) > Date.parse(current.savedAt)
    ) {
      await transaction.store.put(legacy, nextKey);
    }
    await transaction.store.delete(LEGACY_SAVED_REGION_KEY);
  }
  await transaction.done;
};

export async function getSavedRegion(
  spotId?: SpotId,
): Promise<SavedRegionPack | undefined> {
  await migrateLegacySavedRegion();
  const db = await getDatabase();
  if (spotId) {
    const pack: unknown = await db.get("regions", savedRegionKey(spotId));
    return isSavedRegionPack(pack) ? pack : undefined;
  }

  const packs: unknown[] = await db.getAll("regions");
  return packs
    .filter(isSavedRegionPack)
    .sort((left, right) => Date.parse(right.savedAt) - Date.parse(left.savedAt))[0];
}

export async function deleteSavedRegion(spotId: SpotId = DEFAULT_SPOT_ID) {
  await migrateLegacySavedRegion();
  await (await getDatabase()).delete("regions", savedRegionKey(spotId));
}

export async function enqueueReport(report: PendingReport) {
  await (await getDatabase()).put("reports", report);
}

export async function getPendingReports(): Promise<PendingReport[]> {
  return (await getDatabase()).getAll("reports");
}

export async function removePendingReport(id: string) {
  await (await getDatabase()).delete("reports", id);
}

export async function getAnonymousDeviceId() {
  const db = await getDatabase();
  const current = await db.get("metadata", DEVICE_ID_KEY);
  if (typeof current === "string") return current;
  const created = crypto.randomUUID();
  await db.put("metadata", created, DEVICE_ID_KEY);
  return created;
}

/**
 * Personal settings intentionally contain only reusable accessibility filters
 * and explicit favorites. Precise locations and visit history do not belong in
 * this record.
 */
export async function getUserNeeds(): Promise<UserNeeds> {
  try {
    const value: unknown = await (await getDatabase()).get(
      "metadata",
      USER_PREFERENCES_KEY,
    );
    return normalizePreferences(value)?.needs ?? { ...DEFAULT_USER_NEEDS };
  } catch {
    return { ...DEFAULT_USER_NEEDS };
  }
}

export async function saveUserNeeds(needs: UserNeeds): Promise<UserNeeds> {
  const normalizedNeeds = normalizeUserNeeds(needs);
  if (!normalizedNeeds) throw new TypeError("Invalid user needs");

  const db = await getDatabase();
  const transaction = db.transaction("metadata", "readwrite");
  const current =
    normalizePreferences(await transaction.store.get(USER_PREFERENCES_KEY)) ??
    createDefaultPreferences();
  const next: StoredUserPreferences = {
    ...current,
    needs: normalizedNeeds,
  };
  await transaction.store.put(next, USER_PREFERENCES_KEY);
  await transaction.done;
  return { ...next.needs };
}

export async function getFavoriteToiletIds(): Promise<string[]> {
  try {
    const value: unknown = await (await getDatabase()).get(
      "metadata",
      USER_PREFERENCES_KEY,
    );
    return normalizePreferences(value)?.favoriteToiletIds ?? [];
  } catch {
    return [];
  }
}

export async function toggleFavoriteToilet(
  toiletId: string,
): Promise<string[]> {
  const normalizedId = normalizeToiletId(toiletId);
  const db = await getDatabase();
  const transaction = db.transaction("metadata", "readwrite");
  const current =
    normalizePreferences(await transaction.store.get(USER_PREFERENCES_KEY)) ??
    createDefaultPreferences();
  const favoriteToiletIds = current.favoriteToiletIds.includes(normalizedId)
    ? current.favoriteToiletIds.filter((id) => id !== normalizedId)
    : [...current.favoriteToiletIds, normalizedId];
  const next: StoredUserPreferences = {
    ...current,
    favoriteToiletIds,
  };
  await transaction.store.put(next, USER_PREFERENCES_KEY);
  await transaction.done;
  return [...favoriteToiletIds];
}

export async function resetUserPreferences(): Promise<void> {
  await (await getDatabase()).delete("metadata", USER_PREFERENCES_KEY);
}
